import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Terminal, Crosshair } from "lucide-react";
export default function EngineeringPrecisionForIndustrialProgress() {
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const loadScript = (src: string) => {
      return new Promise<void>(resolve => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve();
        document.head.appendChild(script);
      });
    };
    Promise.all([loadScript("https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js"), loadScript("https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js")]).then(() => {
      const gsap = (window as any).gsap;
      if (!gsap) return;
      const ScrollTrigger = (window as any).gsap.plugins && (window as any).gsap.plugins.scrollTrigger ? (window as any).gsap.plugins.scrollTrigger : (window as any).ScrollTrigger;
      if (ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
      }
      const ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "+=4500",
            pin: true,
            scrub: 1,
            markers: false
          }
        });
        tl.to(".hero-hud", {
          opacity: 0,
          y: -50,
          duration: 1
        });
        tl.fromTo(".hero-text-2", {
          y: 50,
          opacity: 0,
          scale: 0.95
        }, {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1.5,
          ease: "power2.out"
        });
        tl.to(".hero-text-2", {
          y: 0,
          duration: 1
        });
        tl.to(".hero-text-2", {
          opacity: 0,
          y: -50,
          duration: 1
        });
        tl.fromTo(".hero-text-3", {
          y: 50,
          opacity: 0
        }, {
          y: 0,
          opacity: 1,
          duration: 1.5,
          ease: "power2.out"
        });
        tl.to(".hero-text-3", {
          y: 0,
          duration: 2
        });
        tl.to(".hero-text-3", {
          opacity: 0,
          y: -50,
          duration: 1
        });
      }, sectionRef);
      return () => ctx.revert();
    });
  }, []);
  return <section ref={sectionRef} data-section-id="224938" id="օրբիտալ-հրամանատարություն" className="text-foreground relative h-screen w-full overflow-hidden flex flex-col justify-between dark bg-slate-950">
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes border-pulse {
            0% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb), 0.4); }
            70% { box-shadow: 0 0 0 15px rgba(var(--color-primary-rgb), 0); }
            100% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb), 0); }
          }
          .animate-border-pulse {
            animation: border-pulse 2s infinite;
          }
        `
    }} />

      {/* Earth Horizon Background - Updated with -inset-4 to prevent GSAP pin gaps */}
      <div className="absolute -inset-4 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[url('https://summary-monarch-dev.10web.cloud/wp-content/uploads/2026/06/orbital_earth_view.jpg')] bg-cover bg-center contrast-[1.15] brightness-[0.45] h-full opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-transparent to-slate-950/90" />
      </div>

      {/* HUD Layer */}
      <div className="hero-hud absolute inset-0 z-10 flex flex-col justify-between p-6 md:p-12 pointer-events-none">
        
        {/* Original Circular HUD on the left */}
        <div className="hidden lg:flex flex-col gap-3 font-default text-[clamp(10px,1.5vw,12px)] text-muted-foreground border border-primary/40 backdrop-blur-md bg-background/20 aspect-square w-[280px] rounded-full justify-center items-center text-center shadow-[0_0_30px_rgba(0,0,0,0.6)] absolute left-8 top-1/2 -translate-y-1/2 overflow-hidden z-20">
          <div className="absolute inset-0 rounded-full shadow-[inset_-15px_-15px_30px_rgba(0,0,0,0.4)] pointer-events-none" />
          
          <div className="flex items-center justify-center gap-2 text-primary z-10 w-full mt-2">
            <Terminal className="size-4" />
            <span>ORBIT.INIT</span>
          </div>
          <div className="space-y-1 z-10 w-full">
            <p className="uppercase tracking-widest text-foreground/60 text-[9px] mb-1">Coordinates</p>
            <p>LAT: 40.1772° N</p>
            <p>LONG: 44.5035° E</p>
            <p>ALT: 408.00 km</p>
          </div>
          <div className="space-y-1 z-10 w-full">
            <p className="uppercase tracking-widest text-foreground/60 text-[9px] mb-1">Data Stream</p>
            <p>BAND: X-BAND</p>
            <p>ENCRYPT: AES-256</p>
          </div>
          <div className="space-y-1 z-10 w-full mb-2">
            <p className="uppercase tracking-widest text-foreground/60 text-[9px] mb-1">System</p>
            <p className="text-primary animate-pulse">● CONNECTED</p>
          </div>
        </div>

        {/* Top: Earth-Curved H1 shifted down into view */}
        <div className="w-full flex flex-col items-center pt-14 md:pt-24">
          <h1 className="w-full max-w-6xl">
            <span className="sr-only">Դիտում Ենք Տիեզերքից</span>
            <svg viewBox="0 0 1200 260" className="w-full h-auto overflow-visible" aria-hidden="true">
              <defs>
                <path id="earth-top-curve" d="M 30,220 Q 600,25 1170,220" fill="transparent" />
                <linearGradient id="text-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="65%" stopColor="#f0f0f0" />
                  <stop offset="100%" stopColor="#94a3b8" />
                </linearGradient>
              </defs>
              <text fill="url(#text-grad)" className="font-serif font-black uppercase tracking-wider" style={{
              fontSize: "82px",
              filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.9))"
            }}>
                <textPath href="#earth-top-curve" startOffset="50%" textAnchor="middle">
                  ԴԻՏՈՒՄ ԵՆՔ ՏԻԵԶԵՐՔԻՑ
                </textPath>
              </text>
            </svg>
          </h1>
        </div>
      </div>

      {/* GSAP Scroll Text Section 2 */}
      <div className="hero-text-2 absolute inset-0 flex flex-col items-center justify-center z-20 text-center opacity-0 pointer-events-none px-4">
        <h2 className="text-4xl md:text-7xl font-bold font-default tracking-widest text-white uppercase">
          Գործում Երկրի Վրա
        </h2>
      </div>

      {/* GSAP Scroll Text Section 3 */}
      <div className="hero-text-3 absolute inset-0 flex flex-col justify-center z-30 px-6 md:px-20 max-w-6xl mx-auto opacity-0 pointer-events-none">
        <div className="border-l-2 border-primary pl-6 md:pl-8 py-2 max-w-4xl">
          <p className="text-[clamp(14px,2.5vw,20px)] text-muted-foreground font-default font-light leading-relaxed">
            «Գեոկոսմոս» ընկերությունն հանդիսանում է արբանյակային նկարահանման առաջատարը շուկայում։ Պատկերները ստացվում են սեփական, ինչպես նաև ոլորտի միջազգային առաջատար գործընկերներից։ Ընկերությունը մասնագիտացած է նաև պատկերների ժամանակակից տեխնոլոգիաներով ֆոտոգրամետրիական մշակման, թվային քարտեզների ստեղծման և թարմացման, ինչպես նաև գեոդեզիական աշխատանքների վրա:
          </p>
        </div>
        <div className="mt-10 ml-6 md:ml-8 pointer-events-auto">
          <div className="relative group inline-block">
            <div className="absolute -inset-1 bg-primary/25 animate-ping opacity-20" />
            <div className="absolute inset-0 border border-primary animate-border-pulse pointer-events-none" />
            <Button asChild size="lg" className="relative rounded-md bg-background/80 backdrop-blur-md border border-primary/50 text-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 h-[clamp(44px,10vw,64px)] px-[clamp(24px,5vw,40px)] text-[clamp(12px,3vw,18px)] font-default tracking-wider uppercase">
              <Link to="#">
                Իմանալ Ավելին
                <Crosshair className="ml-3 size-5 opacity-70 group-hover:rotate-90 transition-transform duration-500" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}